import React from 'react';
import { Col, Container, Row, Card } from 'react-bootstrap';
import FabricBadge from '../components/FabricBadge';
import { Key, Diagram3, Eye, JournalCheck } from 'react-bootstrap-icons';

const modules = [
  {
    icon: <Key size={22} />,
    tag: 'Provisioning Layer',
    title: 'Issue the right credential to the right machine — automatically.',
    text: 'Arkodomo connects to the identity systems your infrastructure already uses: cloud IAM, directory services and SSO, secrets managers, orchestration platforms, CI/CD pipelines, and API key stores. Every machine identity is provisioned through a standardized object model with ownership metadata, policy templates, and automated lifecycle workflows. No bespoke integrations. No rip-and-replace.'
  },
  {
    icon: <Diagram3 size={22} />,
    tag: 'Visibility + Graph Layer',
    title: 'See every machine identity and what it\'s actually doing.',
    text: 'Arkodomo builds a live identity relationship graph from usage telemetry, access patterns, and privilege mapping across your environment. You get answers to the questions traditional IAM can\'t: Which service account calls what? Which are unused? Which are overprivileged? Which violate policy? The graph is the foundation for detection and governance — you can\'t protect what you can\'t see.'
  },
  {
    icon: <Eye size={22} />,
    tag: 'Behavioral Detection',
    title: 'Detect credential threats before they become breaches.',
    text: 'Arkodomo applies ML to the identity graph and usage telemetry to surface threats that signature-based tools miss. Credential abuse patterns, lateral movement signals, privilege escalation anomalies, token usage from unexpected regions, API call frequency spikes — detected early, with context. This is the defensible, hard-to-replicate layer that makes machine identity security a product category, not a feature.'
  },
  {
    icon: <JournalCheck size={22} />,
    tag: 'Lifecycle & Governance',
    title: 'Enforce policy continuously — not quarterly.',
    text: 'Most enterprises fail at machine identity hygiene because governance is manual. Arkodomo automates expiration enforcement, dormant identity cleanup, ownership reassignment detection, policy drift alerting, and compliance audit exports. Security teams get continuous control instead of painful point-in-time reviews. This is where machine identity management becomes machine identity governance.'
  }
];

function Platform() {
  return (
    <>
      {/* Hero Panel */}
      <section className="py-5">
        <Container>
          <Row>
            <Col lg={8}>
              <div className="hero-panel">
                <h1 className="h2 fw-bold text-light mb-2">Arkodomo Platform</h1>
                <div className="mb-3"><FabricBadge variant="built" /></div>
                <p className="text-white-50 mb-0">The identity broker for the agentic era. Arkodomo sits above your existing identity infrastructure — issuing cryptographic identities for every machine and AI agent, building a live relationship graph, detecting behavioral anomalies, and enforcing lifecycle governance across every environment your workloads touch.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Category framing */}
      <section className="py-5 bg-dark bg-opacity-50">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <Row className="g-4 text-center">
                {[
                  { label: 'Machine Identity Security' },
                  { label: 'Non-Human Identity Governance' },
                  { label: 'Zero Trust Infrastructure' },
                  { label: 'AI Agent Identity Management' },
                ].map((c, i) => (
                  <Col key={i} xs={6} md={3}>
                    <div className="p-3 rounded-3" style={{ background: 'rgba(246,196,83,0.07)', border: '1px solid rgba(246,196,83,0.2)' }}>
                      <small className="text-white-50 fw-semibold" style={{ fontSize: '0.78rem', letterSpacing: '0.03em' }}>{c.label}</small>
                    </div>
                  </Col>
                ))}
              </Row>
              <p className="text-center text-white-50 mt-4 mb-0" style={{ fontSize: '0.9rem' }}>Arkodomo operates at the intersection of these four converging markets.</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 4 Platform Modules */}
      <section className="py-5">
        <Container>
          <h2 className="h4 text-light mb-4 section-heading">Four Integrated Layers</h2>
          <Row className="g-4">
            {modules.map((m, i) => (
              <Col key={i} md={6}>
                <Card bg="dark" text="light" className="h-100">
                  <Card.Body className="p-4">
                    <div className="card-icon-badge mb-3">{m.icon}</div>
                    <small className="roadmap-badge now mb-2 d-inline-block">{m.tag}</small>
                    <Card.Title className="h5 mt-1">{m.title}</Card.Title>
                    <Card.Text className="text-white-50 mb-0" style={{ fontSize: '0.9rem', lineHeight: 1.65 }}>{m.text}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Connectors */}
      <section className="py-5 bg-dark bg-opacity-50">
        <Container>
          <h2 className="h4 text-light mb-2 section-heading">Connects to Your Entire Stack</h2>
          <p className="text-white-50 mb-4" style={{ fontSize: '0.95rem' }}>From legacy cloud IAM to cutting-edge AI agent frameworks — Arkodomo connects to the identity systems and protocols you already run. No rip-and-replace, no forklift migrations.</p>
          <Row className="g-3">
            {[
              'Cloud IAM (AWS, Azure, GCP)',
              'Directory Services & SSO',
              'Kubernetes & Orchestration',
              'Secrets Managers',
              'CI/CD Pipelines & GitHub Apps',
              'API Key Stores',
              'On-Prem & Hybrid Environments',
              'SaaS Service Accounts',
              'MCP Servers & AI Agent Frameworks',
              'SPIFFE / SPIRE',
            ].map((connector, i) => (
              <Col key={i} xs={6} md={3}>
                <div className="p-3 rounded-3 h-100" style={{ background: 'rgba(20,30,50,0.7)', border: '1px solid rgba(255,255,255,0.10)' }}>
                  <small className="text-white-50 fw-semibold" style={{ fontSize: '0.8rem' }}>{connector}</small>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </>
  );
}

export default Platform;
